import { useMemo } from 'react';
import { Alert, Box, Button, Card, Modal, Text } from '@nimbus-ds/components';
import { FEATURE_ONLY_LAYOUT_DEFAULT } from 'App/features';
import { NUVEMPAGO_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { SubmitButton } from 'commons/components';
import { Format, dateFormat } from 'commons/utils/date';
import { useHasLimitingFeature } from 'domains/Auth/hooks';
import { useShouldScheduleDowngrade } from 'domains/Billing/BillingPlans/hooks';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { useKycStatus } from 'domains/NuvemPagoCommons/hooks/useKycStatus';
import { ChangePlanModalProps } from '../../ChangePlanModal';
import FeatureList, { Feature } from '../FeatureList/FeatureList';
import PlanChangeVisual from '../PlanChangeVisual';

export default function DowngradeBasicPlanModal({
  show,
  onConfirm,
  onCancel,
  newPlan,
  currentPlan,
  disableConfirm,
}: Readonly<ChangePlanModalProps & { disableConfirm: boolean }>) {
  const t = useTranslationBilling('billingPlansPage.changePlanModal');
  const hasFeature = useHasLimitingFeature(FEATURE_ONLY_LAYOUT_DEFAULT);
  const [hasNuvemPagoEnabled] = useHasTags(NUVEMPAGO_ENABLED);
  const { isKycRejected } = useKycStatus();
  const { shouldScheduleDowngrade, downgradeDate } =
    useShouldScheduleDowngrade();

  const features = useMemo(() => {
    let features: Feature[] = [
      {
        text: t('basic.feature1'),
        checked: true,
      },
      {
        checked: true,
        text:
          !hasNuvemPagoEnabled || isKycRejected
            ? t('basic.feature3')
            : t('basic.feature2'),
      },
    ];

    if (hasFeature) {
      features.push({
        text: t('basic.moreThemes'),
        checked: false,
      });
    }

    features = [
      ...features,
      {
        text: t('basic.feature4'),
        checked: false,
      },
      {
        text: t('basic.feature5'),
        checked: false,
      },
      {
        text: t('basic.feature6'),
        checked: false,
      },
      {
        text: t('basic.feature7'),
        checked: false,
      },
      {
        text: t('basic.feature8'),
        checked: false,
      },
    ];

    if (currentPlan?.posExtraInfo) {
      features.push({
        text: t('basic.posFeature'),
        checked: false,
      });
    }

    return features;
  }, [t, hasFeature, hasNuvemPagoEnabled, isKycRejected, currentPlan]);

  return (
    <Modal open={show} onDismiss={onCancel}>
      <Modal.Header title={t('basic.title', { newPlanName: newPlan?.name })} />
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="2">
          {shouldScheduleDowngrade && downgradeDate ? (
            <>
              <Text>
                {t('basic.scheduled.message1', {
                  newPlanName: newPlan?.name,
                  expirationDate: dateFormat(downgradeDate, Format.DD_MM),
                })}
              </Text>
              <Text>{t('basic.scheduled.message2')}</Text>
            </>
          ) : (
            <Text>
              {t('basic.message', {
                newPlanName: newPlan?.name,
              })}
            </Text>
          )}
          <Card>
            <Box display="flex" flexDirection="column" gap="4">
              {currentPlan && newPlan && (
                <PlanChangeVisual
                  firstPlan={currentPlan}
                  secondPlan={newPlan}
                />
              )}

              <Alert
                title={t('alert.title', {
                  planName: newPlan?.name,
                })}
                appearance="danger"
                show={hasFeature}
              >
                {t('alert.description')}
              </Alert>

              <FeatureList features={features} />
            </Box>
          </Card>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <SubmitButton isLoading={disableConfirm} onClick={onConfirm}>
          {t('basic.confirm')}
        </SubmitButton>
      </Modal.Footer>
    </Modal>
  );
}
